/**
 * This is copied from the apollo-client-persist examples:
 * https://github.com/apollographql/apollo-cache-persist/tree/master/examples/react-native/src/utils/persistence
 */
import { visit } from 'graphql';
import {
	ApolloLink,
	type FetchResult,
	type NextLink,
	type Observable,
	type Operation,
} from '@apollo/client/core';
import traverse from 'traverse';

import { extractPersistDirectivePaths } from './transform';
import { type NotWorthIt } from '@/types/utils';

/**
 * Given a data result object path, return the equivalent query selection path.
 *
 * @param path The data result object path. i.e.: ["a", 0, "b"]
 * @return the query selection path. i.e.: "a.b"
 */
const toQueryPath = (path: NotWorthIt[]): string =>
	path.filter((key) => isNaN(Number(key))).join('.');

/**
 * Given a data result object, attach __persist values.
 */
const attachPersists = (paths: NotWorthIt[], object: FetchResult['data']) => {
	const queryPaths = paths.map(toQueryPath);

	// eslint-disable-next-line array-callback-return
	return traverse(object).map(function mapOverNodes() {
		if (
			!this.isRoot &&
			this.node &&
			typeof this.node === 'object' &&
			Object.keys(this.node).length &&
			!Array.isArray(this.node)
		) {
			const path = toQueryPath(this.path);

			this.update({
				__persist: Boolean(
					queryPaths.find(
						(queryPath) =>
							queryPath.indexOf(path) === 0 ||
							path.indexOf(queryPath) === 0
					)
				),
				...this.node,
			});
		}
	});
};

class PersistLink extends ApolloLink {
	public directive = 'persist';

	/**
	 * Link query requester.
	 */
	request(
		operation: Operation,
		forward?: NextLink
	): Observable<FetchResult> | null {
		const { query, paths } = extractPersistDirectivePaths(
			operation.query,
			this.directive
		);
		// Replace query with one without @persist directives.
		operation.query = query;

		// Remove requesting __persist fields.
		operation.query = visit(operation.query, {
			// eslint-disable-next-line consistent-return
			Field: ({ name: { value: name } }) => {
				if (name === '__persist') {
					return null;
				}
			},
		});

		return (
			forward?.(operation).map((result) => {
				if (result.data) {
					result.data = attachPersists(paths, result.data);
				}

				return result;
			}) ?? null
		);
	}
}

const createPersistLink = () => new PersistLink();

export { PersistLink, createPersistLink };
